import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";

export const EditDialog = ({ title, endpoint, fields, initial, onSaved, testId }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (open) setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(endpoint, form);
      toast.success("Profile updated");
      setOpen(false);
      await onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testId}-open`}
          className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
        >
          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold tracking-tight">
            {title}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5" data-testid={`${testId}-form`}>
          {fields.map(([key, label, type = "text", required = true]) => (
            <div key={key}>
              <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                {label}
              </Label>
              <Input
                type={type}
                required={required}
                value={form[key] ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    [key]: type === "tel" ? formatPhone(e.target.value) : e.target.value,
                  })
                }
                data-testid={`${testId}-${key}-input`}
                className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
              />
            </div>
          ))}
          <DialogFooter>
            <Button
              type="submit"
              disabled={busy}
              data-testid={`${testId}-submit`}
              className="h-11 w-full rounded-md bg-blue-600 font-semibold hover:bg-blue-700"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
